import CloseIcon from "@mui/icons-material/Close";
import {Alert, Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Stack} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {getIconForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import React, {useId, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {DateRange} from "../../common/newDatePicker/datePickerShared";
import {createDefaultInsightsRange} from "../../insights/v2/insightsDateRangePresets";
import {getSectorIcon} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";
import {useGuestCategories} from "../../guestCategories/useGuestCategories";
import {
    availableSurveyFilters,
    SurveyFilterDescriptor,
    surveyFilterDescriptors,
    SurveyFilterId,
} from "./surveyFilters";

const createDefaultEvaluationRange = (): DateRange => createDefaultInsightsRange();

export type SurveyEvaluationFilterState = {
    range: DateRange
    compareRange: DateRange | null
    languages: string[]
    sectorIds: string[]
    reviewStates: SurveyReviewState[]
    guestCategoryId: string | null
    versions: number[]
    minSubmissions: number | null
};

export const createDefaultEvaluationFilters = (): SurveyEvaluationFilterState => ({
    range: createDefaultEvaluationRange(),
    compareRange: null,
    languages: [],
    sectorIds: [],
    reviewStates: [],
    guestCategoryId: null,
    versions: [],
    minSubmissions: null,
});

interface Props {
    value: SurveyEvaluationFilterState
    onChange: (next: SurveyEvaluationFilterState) => void
    languageOptions: string[]
    sectorOptions: Sector[]
    currentVersion?: number
    layout?: 'bar' | 'stacked'
}

const FLAG_WIDTH = 21;
const FLAG_HEIGHT = 14;

const ICON_SLOT = 18;

interface LanguageFlagProps {
    code: string
}

function LanguageFlag({code}: LanguageFlagProps) {
    const src = getIconForLanguage(code);

    return (
        <Box
            component={src ? 'img' : 'span'}
            src={src}
            alt={''}
            sx={theme => ({
                display: 'block',
                flexShrink: 0,
                boxSizing: 'border-box',
                width: FLAG_WIDTH,
                height: FLAG_HEIGHT,
                objectFit: 'cover',
                borderRadius: 0.5,
                border: src ? `1px solid ${alpha(theme.palette.text.primary, 0.15)}` : 'none',
            })}
        />
    );
}

interface SectorIconProps {
    sector?: Sector
}

function SectorIcon({sector}: SectorIconProps) {
    const Icon = getSectorIcon(sector);

    if (!Icon) return <Box component={'span'} sx={{width: ICON_SLOT, flexShrink: 0}}/>;

    return <Icon sx={{fontSize: ICON_SLOT, color: 'text.secondary', flexShrink: 0}}/>;
}

interface SurveyFilterSelectProps {
    descriptor: SurveyFilterDescriptor
    value: SurveyEvaluationFilterState
    onChange: (next: SurveyEvaluationFilterState) => void
    stacked: boolean
    renderIcon?: (optionValue: string) => React.ReactNode
}

function SurveyFilterSelect({descriptor, value, onChange, stacked, renderIcon}: SurveyFilterSelectProps) {
    const {t} = useTranslation();
    const labelId = useId();
    const selected = descriptor.valueOf(value) ?? '';

    const optionLabel = (optionValue: string) =>
        descriptor.options.find(option => option.value === optionValue)?.label
            ?? (descriptor.id === 'guestCategory' ? t('Unavailable guest category') : optionValue);

    const row = (optionValue: string, label: string) => (
        <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minWidth: 0}}>
            {renderIcon?.(optionValue)}
            <Box component={'span'} sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {label}
            </Box>
        </Stack>
    );

    return (
        <FormControl
            size={'small'}
            sx={{width: stacked ? '100%' : {xs: '100%', sm: 180}, minWidth: 0}}
        >
            <InputLabel id={labelId}>{descriptor.label}</InputLabel>
            <Select
                labelId={labelId}
                label={descriptor.label}
                value={selected}
                onChange={event => onChange(descriptor.patch(value, (event.target.value as string) || null))}
                renderValue={picked => row(picked as string, optionLabel(picked as string))}
            >
                {selected && !descriptor.options.some(option => option.value === selected) && (
                    <MenuItem value={selected} disabled>{optionLabel(selected)}</MenuItem>
                )}
                {descriptor.options.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {row(option.value, option.label)}
                    </MenuItem>
                ))}

                {!!selected && <Divider/>}
                {!!selected && (
                    <MenuItem value={''} sx={{gap: 1, color: 'text.secondary'}}>
                        <CloseIcon sx={{fontSize: 17}}/>
                        {t('Remove filter')}
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
}

function SurveyEvaluationFilters({
    value,
    onChange,
    languageOptions,
    sectorOptions,
    currentVersion,
    layout = 'bar'
}: Props) {
    const {t} = useTranslation();
    const categoriesQuery = useGuestCategories();

    const descriptors = useMemo(
        () => availableSurveyFilters(surveyFilterDescriptors({
            languageOptions,
            sectorOptions,
            currentVersion,
            guestCategories: categoriesQuery.categories,
            t,
        })),
        [languageOptions, sectorOptions, currentVersion, categoriesQuery.categories, t]
    );

    const renderIconFor = (descriptorId: SurveyFilterId) => {
        if (descriptorId === 'language') return (code: string) => <LanguageFlag code={code}/>;

        if (descriptorId === 'sector') {
            return (sectorId: string) => (
                <SectorIcon sector={sectorOptions.find(sector => sector.id === sectorId)}/>
            );
        }

        return undefined;
    };

    const stacked = layout === 'stacked';

    return (
        <Stack gap={1.5}>
            <Stack direction={stacked ? 'column' : 'row'} flexWrap={stacked ? undefined : 'wrap'} gap={1.5}>
                {descriptors.map(descriptor => (
                    <SurveyFilterSelect
                        key={descriptor.id}
                        descriptor={descriptor}
                        stacked={stacked}
                        value={value}
                        onChange={onChange}
                        renderIcon={renderIconFor(descriptor.id)}
                    />
                ))}
            </Stack>

            {categoriesQuery.isError && (
                <Alert
                    severity={'error'}
                    action={(
                        <Button color={'inherit'} onClick={() => categoriesQuery.refetch()}>
                            {t('Retry')}
                        </Button>
                    )}
                >
                    {t('Could not load guest categories.')}
                </Alert>
            )}

        </Stack>
    );
}

export default SurveyEvaluationFilters;
